<template>
  <div class="panel" data-qid="engagement-team">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="user-cog" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('projects.engagement.team.title') }}
              <span class="section-compact__secondary"></span>
            </div>
          </div>
        </div>
      </div>

      <div class="profile-compact__actions">
        <span class="icon-btn"
              data-qid="engagement-team-add-button"
              v-if="canManageTeam"
              v-b-tooltip.hover
              :title="$t('projects.engagement.team.add')"
              @click="showAddModal"
        >
          <font-awesome-icon icon="plus"/>
        </span>
      </div>
    </header>

    <div class="panel__body">
      <div v-if="project.users && project.users.length" class="card-list" data-qid="engagement-team-list">
        <div class="card-row"
             v-for="(user, index) in sortedUsers"
             :key="'engagement-team-' + user.id"
             :id="'engagement-team-row-' + index"
        >
          <div class="card-row__main">
            <div class="card-row__title">
              <span class="text-truncate">
                <Avatar :object="user" size="xs" class="mr-2"></Avatar>
                {{ user.name }}
              </span>

              <span class="card-row__meta engagement-team__pills">
                <span v-if="project.include_independence_declaration" class="due-pill engagement-team__independence" :class="'independence-' + independenceKey(user)">
                  <font-awesome-icon :icon="independenceIcon(user)" class="mr-1" />
                  <span>{{ $t('projects.view.independence.status.' + independenceKey(user)) }}</span>
                </span>

                <b-dropdown v-if="canManageTeam"
                            size="sm"
                            variant="link"
                            toggle-class="engagement-role-pill"
                            :class="'engagement-role engagement-role--' + roleOf(user)"
                            no-caret
                            data-qid="engagement-team-role-dropdown"
                >
                  <template #button-content>
                    {{ $t('projects.engagement.role.' + roleOf(user)) }}
                    <font-awesome-icon icon="chevron-down" class="ml-1"/>
                  </template>
                  <b-dropdown-item v-for="role in roles"
                                   :key="role"
                                   :active="roleOf(user) === role"
                                   @click="changeRole(user, role)"
                  >
                    {{ $t('projects.engagement.role.' + role) }}
                  </b-dropdown-item>
                </b-dropdown>
                <span v-else class="engagement-role-pill" :class="'engagement-role--' + roleOf(user)">
                  {{ $t('projects.engagement.role.' + roleOf(user)) }}
                </span>
              </span>
            </div>
          </div>

          <div class="profile-compact__actions">
            <b-button
                v-if="canManageTeam"
                variant="link"
                class="icon-btn unlink-btn"
                qid="engagement-team-remove-button"
                v-b-tooltip.hover
                :title="$t('projects.engagement.team.remove')"
                @click.stop.prevent="showRemoveModal(user)"
            >
              <font-awesome-icon icon="link" class="unlink-btn__idle" />
              <font-awesome-icon icon="unlink" class="unlink-btn__hover" />
            </b-button>
          </div>
        </div>
      </div>
      <div v-else class="section-container">
        <span class="text-muted">
          {{ $t('system.missing_input') }}
        </span>
      </div>
    </div>

    <!-- Add team member modal -->
    <b-modal ref="add-team-member-modal" modal-class="task-modal" @hide="hideAddModal">
      <template #modal-header>
        <div class="" style="width: 100%;">
          <span @click="hideAddModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" data-qid="engagement-team-add-title">
            {{ $t('projects.engagement.team.add') }}
          </h5>
        </div>
      </template>

      <div class="form-container">
        <b-alert v-if="error.length"
                 show
                 variant="danger"
                 data-qid="engagement-team-add-error">
          {{ error }}
        </b-alert>

        <b-form-group>
          <label data-qid="engagement-team-users-label">
            {{ $t('projects.engagement.team.users_label') }}<span class="mandatory"> *</span>
          </label>

          <div class="multiselect-row">
            <multiselect v-model="selectedUsers"
                         :options="userOptions"
                         :multiple="true"
                         :taggable="false"
                         :internal-search="false"
                         :disabled="buttonDisabled"
                         track-by="slug"
                         label="name"
                         :select-label="$t('system.dropdown_select_label')"
                         :selected-label="$t('system.dropdown_selected_label')"
                         :deselect-label="$t('system.dropdown_deselect_label')"
                         :placeholder="$t('projects.engagement.team.users_label')"
                         @search-change="loadUsers"
                         data-qid="engagement-team-users-select"
            >
              <template slot="tag" slot-scope="props">
                <span class="mr-3 user-tag">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">{{ props.option.name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <UserDropdownOption :user="props.option"></UserDropdownOption>
              </template>
            </multiselect>

            <UserInviteModal :buttonType="'small'" :buttonDisabled="buttonDisabled" @reloadUserList="loadUsers()">
            </UserInviteModal>
          </div>
        </b-form-group>

        <b-form-group>
          <label data-qid="engagement-team-role-label">
            {{ $t('projects.engagement.team.role_label') }}<span class="mandatory"> *</span>
          </label>
          <div class="engagement-role-options">
            <label v-for="role in roles"
                   :key="'role-option-' + role"
                   class="engagement-role-option"
                   :class="{'engagement-role-option--selected': selectedRole === role}"
            >
              <input type="radio"
                     name="engagement-role"
                     :value="role"
                     v-model="selectedRole"
                     :data-qid="'engagement-team-role-' + role">
              <span>
                <span class="engagement-role-option__name">{{ $t('projects.engagement.role.' + role) }}</span>
                <span class="engagement-role-option__description">{{ $t('projects.engagement.role_description.' + role) }}</span>
              </span>
            </label>
          </div>
        </b-form-group>
      </div>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="hideAddModal" data-qid="engagement-team-add-cancel">
                {{ $t('projects.create.cancel') }}
              </b-button>
              <b-button class="ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        @click="addMembers"
                        data-qid="engagement-team-add-submit"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('projects.create.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Remove team member modal -->
    <b-modal ref="remove-team-member-modal" modal-class="task-modal" @hide="hideRemoveModal">
      <template #modal-header>
        <div class="" style="width: 100%;">
          <span @click="hideRemoveModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" data-qid="engagement-team-remove-title">
            {{ $t('projects.engagement.team.remove') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               data-qid="engagement-team-remove-error">
        {{ error }}
      </b-alert>

      <div v-if="loadingAssignments" class="text-center text-muted">
        <font-awesome-icon icon="spinner" class="fa-spin"/>
      </div>
      <div v-else>
        <p class="text-center" v-if="!removeTaskCount">
          {{ $t('projects.engagement.team.remove_message', { name: selectedUser.name }) }}
        </p>
        <div v-else>
          <b-alert show variant="warning" data-qid="engagement-team-remove-tasks-warning">
            {{ $t('projects.engagement.team.remove_with_tasks_message', { name: selectedUser.name, count: removeTaskCount }) }}
          </b-alert>
        </div>
      </div>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="hideRemoveModal" data-qid="engagement-team-remove-cancel">
                {{ $t('projects.create.cancel') }}
              </b-button>
              <b-button class="ml-3"
                        variant="danger"
                        :disabled="buttonDisabled || loadingAssignments"
                        @click="removeMember"
                        data-qid="engagement-team-remove-submit"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                <span v-if="removeTaskCount">{{ $t('projects.engagement.team.remove_with_tasks_submit') }}</span>
                <span v-else>{{ $t('projects.engagement.team.remove_submit') }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';
import UserDropdownOption from '@/components/UserDropdownOption.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import { ROLES, engagementPermissions } from '@/views/Projects/Engagement/engagement.js';

export default {
  name: 'EngagementTeam',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    UserDropdownOption,
    Avatar,
    UserInviteModal
  },
  data: function() {
    return {
      roles: ROLES,
      users: [],
      selectedUsers: [],
      selectedRole: '',
      selectedUser: {},
      removeTaskCount: 0,
      loadingAssignments: false,
      buttonDisabled: false,
      error: ''
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    canManageTeam() {
      return !!engagementPermissions(this.project).manage_team && this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    userOptions() {
      // Team members can't be added twice; their role is changed in the list instead
      const existingIds = _.map(this.project.users || [], 'id');
      return _.filter(this.users, user => !existingIds.includes(user.id));
    },
    sortedUsers() {
      // Managers first, then associates, each alphabetically
      return _.sortBy(this.project.users || [], [user => this.roleOf(user) === 'manager' ? 0 : 1, 'name']);
    }
  },
  methods: {
    roleOf(user) {
      return (this.project.users_roles && this.project.users_roles[user.id]) || 'manager';
    },
    independenceKey(user) {
      const value = this.project.users_independence ? this.project.users_independence[user.id] : null;
      if (value === 1) {
        return 'approved';
      }
      if (value === 2) {
        return 'rejected';
      }
      return 'pending';
    },
    independenceIcon(user) {
      const icons = { approved: 'check-circle', rejected: 'times-circle', pending: 'exclamation-triangle' };
      return icons[this.independenceKey(user)];
    },
    loadUsers(query) {
      let self = this;
      piincHttp.get('users', { params:
        {
          client: this.getClient.id,
          keyword: query ? query : undefined,
          per_page: 100
        }
      }).then(function(response) {
        self.users = response.data.items;
      }, function() {});
    },
    showAddModal() {
      this.error = '';
      this.selectedUsers = [];
      this.selectedRole = '';
      this.loadUsers();
      this.$refs['add-team-member-modal'].show();
    },
    hideAddModal() {
      this.error = '';
      this.selectedUsers = [];
      this.selectedRole = '';
      this.$refs['add-team-member-modal'].hide();
    },
    addMembers() {
      let self = this;

      if (!this.selectedUsers.length) {
        this.error = this.$t('projects.engagement.team.users_required');
        return;
      }
      if (!this.selectedRole) {
        this.error = this.$t('projects.engagement.team.role_required');
        return;
      }

      this.error = '';
      this.buttonDisabled = true;
      piincHttp.post('projects/' + this.project.slug + '/team', {
        users: _.map(this.selectedUsers, 'id'),
        role: this.selectedRole
      }).then(function() {
        self.buttonDisabled = false;
        self.hideAddModal();
        self.$toastr('success', self.$t('projects.engagement.team.add_success'));
        self.$emit('updated');
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    changeRole(user, role) {
      let self = this;
      if (this.roleOf(user) === role) {
        return;
      }

      piincHttp.put('projects/' + this.project.slug + '/team/' + user.slug, {
        role: role
      }).then(function() {
        self.$toastr('success', self.$t('projects.engagement.team.role_success'));
        self.$emit('updated');
      }, function(error) {
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    showRemoveModal(user) {
      let self = this;
      this.error = '';
      this.selectedUser = user;
      this.removeTaskCount = 0;
      this.loadingAssignments = true;
      this.$refs['remove-team-member-modal'].show();

      piincHttp.get('projects/' + this.project.slug + '/team/' + user.slug + '/assignments').then(function(response) {
        self.removeTaskCount = response.data.main_task_count;
        self.loadingAssignments = false;
      }, function(error) {
        self.loadingAssignments = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    hideRemoveModal() {
      this.error = '';
      this.selectedUser = {};
      this.$refs['remove-team-member-modal'].hide();
    },
    removeMember() {
      let self = this;
      this.buttonDisabled = true;

      piincHttp.delete('projects/' + this.project.slug + '/team/' + this.selectedUser.slug, { params:
        {
          remove_assignments: this.removeTaskCount ? 1 : undefined
        }
      }).then(function() {
        self.buttonDisabled = false;
        self.hideRemoveModal();
        self.$toastr('success', self.$t('projects.engagement.team.remove_success'));
        self.$emit('updated');
      }, function(error) {
        self.buttonDisabled = false;
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
